import { Button, message } from "antd";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  employeesImporter,
  friendlyImportEmployeesError,
  getImportEmployeesErrorMessage,
} from "../../../lib/employeesImporter";

/**
 * Component for importing employees from an Excel or CSV file.
 * After a successful import, clears the buffer and the input to allow new imports without blocking.
 * Solves the problem that the same file cannot be selected twice in a row.
 * Also clears the file input each time the modal is closed.
 */
const EmployeeImporter = ({ idCompany, onImportSuccess, isModalOpen }) => {
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null); // Reference to clear the file input

  /**
   * Helper function to clear the file input and buffer.
   * This function can be called explicitly from the parent or after importing.
   * Only the empty string can be set programmatically as the value of a file input.
   */
  const limpiarInputYBuffer = () => {
    setFile(null);
    // Only set the value to the empty string, as required by browser security
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.files = null
    }
  };


  /**
   * Handles the change of the selected file.
   * If the user selects the same file twice in a row, forces the input to reset.
   * @param {Event} e - File change event.
   */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
 
  };

  /**
   * Validates employee data.
   * @param {Object} employee - Employee object.
   * @returns {Array} - List of errors found.
   */
  const validateEmployee = (employee) => {
    const errors = [];
    if (!employee.cuil || isNaN(employee.cuil)) errors.push("Cuil es obligatorio y debe ser numérico.");
    if (!employee.dni || isNaN(employee.dni)) errors.push("DNI es obligatorio y debe ser numérico.");
    if (!employee.salary || isNaN(employee.salary)) errors.push("Salario es obligatorio y debe ser numérico.");
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (employee.email && !emailPattern.test(employee.email)) errors.push("Email no tiene un formato válido.");
    return errors;
  };

  /**
   * Handles the upload and processing of the selected file.
   * Clears the buffer and input after a successful or failed import.
   */
  const handleFileUpload = async () => {
    // If no file is selected, show error
    if (!file) {
      message.error("Por favor, selecciona un archivo antes de subirlo.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // Mapeo de cabeceras legibles a claves internas
        // Puedes agregar más mapeos según tus necesidades
        const headerMap = {
          "fecha de alta": "fecha_alta",
          "fecha de baja": "fecha_baja",
          "fecha de afiliacion": "fecha_alta", // Mapeo para Excel ARL
          "tipo aporte": "tipoAporte",
          "compania Cuit": "companiaCuit",
          "compañia":"companyName",
          "nombre": "name",
          "apellido y nombre": "name",
          "apellido": "apellido", // Mapeo para Excel ARL
          "salario": "salary",
          "motivo": "reason",
          "estado": "status",
          "numero de afiliado": "numero_afiliado",
          "nro de afiliado": "numero_afiliado", // Mapeo para Excel ARL
          "cargo": "charge",
          "telefono": "phoneNumber",
          "calle": "calle",
          "numero": "numero",
          "direccion": "domicilio", // Mapeo para Excel ARL
          "localidad": "ciudad",
          "provincia": "provincia",
          "partido": "partido",
          // Agrega más si es necesario
        };

        // Función para normalizar las claves de un objeto según el headerMap
        const normalizeKeys = (obj) => {
          const newObj = {};
          Object.keys(obj).forEach((key) => {
            // Elimina espacios y minúsculas para comparar
            const normalizedKey = key.trim().toLowerCase();
            if (headerMap[normalizedKey]) {
              newObj[headerMap[normalizedKey]] = obj[key];
            } else {
              newObj[normalizedKey] = obj[key];
            }
          });
          
          return newObj;
        };


        // Convierte la hoja a JSON y normaliza las claves
        const employeesRaw = XLSX.utils.sheet_to_json(sheet);
        const employees = employeesRaw.map(normalizeKeys);
        
        // Combinar nombre y apellido si vienen separados (formato Excel ARL)
        const employeesWithName = employees.map((emp) => {
          if (emp.nombre && emp.apellido && !emp.name) {
            emp.name = `${emp.nombre} ${emp.apellido}`.trim();
          }
          return emp;
        });
        
        const validatedEmployees = employeesWithName.map((emp) => ({
          ...emp,
          idCompany: idCompany ? idCompany : emp.companiaID ?? 0,
          errors: validateEmployee(emp),
        }));

        // Filtra empleados con errores de validación
        const employeesWithErrors = validatedEmployees.filter((emp) => emp.errors.length > 0);
        if (employeesWithErrors.length > 0) {
          // Muestra un mensaje de error para cada empleado con errores
          employeesWithErrors.forEach((emp) =>
            toast.error(`Errores para ${emp.name || "empleado desconocido"}: ${emp.errors.join(", ")}`)
          );
          // Si hay errores, detiene la importación y limpia el input
          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        await employeesImporter(validatedEmployees);
        limpiarInputYBuffer();
        toast.success("Empleados importados exitosamente");

        if (onImportSuccess) onImportSuccess();
      } catch (error) {
        const raw = getImportEmployeesErrorMessage(error);
        toast.error(friendlyImportEmployeesError(raw), { autoClose: 12000 });
        limpiarInputYBuffer();
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <div>
      {/* The ref allows clearing the input after importing */}
      <input
        type="file"
        accept=".xlsx, .csv"
        onChange={handleFileChange}
        style={{ marginBottom: 16 }}
        ref={fileInputRef}
      />
      <br/>
      <Button type="primary" onClick={handleFileUpload}>
        Importar Empleados
      </Button>

      {/* Show the note only if idCompany is not null or undefined */}
      {(idCompany !== null && idCompany !== undefined) && (
        <p style={{ color: "red", fontSize: "12px", paddingTop: "10px" }}>
          <b>Nota:</b> La importación de empleados solo actualiza datos de empleados existentes o crea nuevos,
          <strong> NO elimina</strong> empleados que no están en la planilla a importar. <br />
          La eliminación de empleados de la nómina debe hacerse manualmente. Esto puede afectar el monto a facturar.
        </p>
      )}
      {(idCompany === null && idCompany === undefined) && (
        <p style={{ color: "red", fontSize: "12px", paddingTop: "10px" }}>
          <b>Recuerde:</b> La importación de empleados solo actualiza datos de empleados existentes o crea nuevos, NO ELIMINA
        </p>
      )}
    </div>
  );
};

export default EmployeeImporter;
